import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { SubgraphTrafficSparkLinesFragment } from 'src/lib/graphqlTypes/types';

import { TRAFFIC_SOURCE_METRIC_KEYS, TrafficSourceSortMetric } from './types';

export const subgraphTrafficSparkLinesFragment = gql`
  fragment SubgraphTrafficSparkLinesFragment on GraphVariant {
    id
    sparklines: subgraphInsightsTimeseries(
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      resolution: $resolution
      requireLatencyMetrics: true
    ) {
      roundedUpTo
      roundedDownFrom
      records {
        timestamp
        data {
          fetchCountPerMin
          fetchErrorPercentage
          serviceTimeP95Ms
        }
      }
    }
  }
`;

/**
 * A set of metrics derived from sparkline data.
 */
type SparklineMetrics = {
  sparklineMetrics: Record<TrafficSourceSortMetric, number[]>;
  min: Record<TrafficSourceSortMetric, number | undefined>;
  max: Record<TrafficSourceSortMetric, number | undefined>;
  average: Record<TrafficSourceSortMetric, number | undefined>;
};

/**
 * The sparkline data to calculate the metrics for.
 */
type SparklineData =
  | SubgraphTrafficSparkLinesFragment['sparklines']
  | null
  | undefined;

/**
 * Calculates a set of metrics for the given sparkline data.
 *
 * @param sparklineData - The sparkline data to calculate the metrics for.
 * @returns The sparkline metrics, the minimum value for each metric, and the maximum value for each metric.
 */
function calculateSparklineMetrics(
  sparklineData: SparklineData,
): SparklineMetrics {
  const sparklineMetrics: Record<TrafficSourceSortMetric, number[]> = {
    fetchCountPerMin: [],
    fetchErrorPercentage: [],
    serviceTimeP95Ms: [],
  };
  const min: Record<TrafficSourceSortMetric, number | undefined> = {
    fetchCountPerMin: undefined,
    fetchErrorPercentage: undefined,
    serviceTimeP95Ms: undefined,
  };
  const max: Record<TrafficSourceSortMetric, number | undefined> = {
    fetchCountPerMin: undefined,
    fetchErrorPercentage: undefined,
    serviceTimeP95Ms: undefined,
  };
  const total: Record<TrafficSourceSortMetric, number | undefined> = {
    fetchCountPerMin: undefined,
    fetchErrorPercentage: undefined,
    serviceTimeP95Ms: undefined,
  };

  sparklineData?.records.forEach((r) => {
    TRAFFIC_SOURCE_METRIC_KEYS.forEach((metric: TrafficSourceSortMetric) => {
      const metricVal = r.data[metric];
      if (typeof metricVal === 'number')
        sparklineMetrics[metric]?.push(metricVal);
      // we choose to look at values above 0 in order to display statistics
      // that have meaning. This can mean that the error graph shows a single
      // figure range with a percentage increase from 0, which is acceptable,
      // since the range only takes into account queries with errors
      const minMetric = min[metric];
      const maxMetric = max[metric];
      if (
        typeof metricVal === 'number' &&
        metricVal > 0 &&
        (!minMetric || minMetric > metricVal)
      ) {
        min[metric] = metricVal;
      }
      if (
        typeof metricVal === 'number' &&
        metricVal > 0 &&
        (!maxMetric || maxMetric < metricVal)
      ) {
        max[metric] = metricVal;
      }
      if (typeof metricVal === 'number') {
        total[metric] = (total[metric] || 0) + metricVal;
      }
    });
  });

  const average: Record<TrafficSourceSortMetric, number | undefined> = {
    fetchCountPerMin:
      sparklineData?.records.length && total.fetchCountPerMin !== undefined
        ? total.fetchCountPerMin / sparklineData.records.length
        : undefined,
    fetchErrorPercentage:
      sparklineData?.records.length && total.fetchErrorPercentage !== undefined
        ? total.fetchErrorPercentage / sparklineData.records.length
        : undefined,
    serviceTimeP95Ms:
      sparklineData?.records.length && total.serviceTimeP95Ms !== undefined
        ? total.serviceTimeP95Ms / sparklineData.records.length
        : undefined,
  };

  return {
    sparklineMetrics,
    min,
    max,
    average,
  };
}

/**
 * Calculates a memoized set of sparkline metrics for the given sparkline data.
 *
 * @param sparklineData - The sparkline data to calculate the metrics for.
 * @returns The sparkline metrics, the minimum value for each metric,
 * the maximum value for each metric, and the average value for each metric.
 */
export function useSparklineMetrics(
  sparklineData: SparklineData,
): SparklineMetrics {
  return useMemo(
    () => calculateSparklineMetrics(sparklineData),
    [sparklineData],
  );
}
